/**
 * Single source of truth for résumé content.
 *
 * The site previously duplicated these facts across HeroSection, AboutSection,
 * ExperienceSection, SkillsSection and the Footer, which is how the site drifted
 * out of sync with the PDF ("8+ years" vs "9+", "Kepler Saving" vs "Kepler
 * Savings", missing Pinnacle work). Update this file and the PDF together.
 */

export const RESUME_PDF_PATH = "/Jarrod_Savard_Resume.pdf";

export const PROFILE = {
    name: "Jarrod Savard",
    headline: "Senior Full-Stack & Founding Engineer",
    subheadline: "Product Engineering | Backend Systems | AI/LLM",
    yearsExperience: "9+",
    location: "Los Angeles, CA",
    email: "Jarrodsavard2x180@gmail.com",
    phone: "(310) 953-7628",
    phoneHref: "tel:+13109537628",
    linkedin: "https://www.linkedin.com/in/jarrod-savard",
    linkedinLabel: "linkedin.com/in/jarrod-savard",
    github: "https://github.com/JarrodSavard",
    githubLabel: "github.com/JarrodSavard",
    site: "https://jarrodsavard.com",
    bookingUrl: "https://calendar.app.google/93Kuv4iG4EUsgXh77",
    bookingLabel: "Book a 30-min call",
} as const;

export const SUMMARY = [
    "Founding engineer and engineering leader with 9+ years building zero-to-one products at early-stage startups. I own systems end to end, from prototyping with the CEO to architecture, backend, frontend, and production infrastructure, while building the engineering operating systems (process, standards, culture) that let small teams ship like larger ones.",
    "Recent results include a caching strategy that cut company-wide AI costs ~60%, a 16s-to-sub-3s performance turnaround, and the technical milestones behind a $2.5M seed raise. I have mentored and developed 15+ engineers.",
    "I thrive in the dynamic environment of early-stage companies where technical decisions directly impact business outcomes.",
];

export interface Metric {
    value: string;
    label: string;
    detail: string;
}

/** The four numbers that carry the most weight with a hiring manager. */
export const METRICS: Metric[] = [
    {
        value: "~60%",
        label: "AI cost reduction",
        detail: "Company-wide, via a production prompt-caching strategy",
    },
    {
        value: "16s → <3s",
        label: "Load time turnaround",
        detail: "Critical cross-team pages, via database indexing",
    },
    {
        value: "$2.5M",
        label: "Seed round supported",
        detail: "Delivered the architecture, roadmap and technical milestones",
    },
    {
        value: "15+",
        label: "Engineers mentored",
        detail: "Across companies and volunteer programs",
    },
];

export interface Highlight {
    title: string;
    body: string;
}

export const HIGHLIGHTS: Highlight[] = [
    {
        title: "Business impact",
        body: "Delivered the architecture, roadmap, and technical milestones that directly supported a $2.5M seed raise; cut company-wide AI costs ~60% and took critical page loads from 16s to under 3s.",
    },
    {
        title: "Founding-stage ownership",
        body: "Joined Pinnacle as the second person, hired as design/frontend engineer, and expanded to own most of the backend, prototyping, and product as the company scaled to 8-10+ people.",
    },
    {
        title: "Engineering leadership",
        body: "Operated as de facto engineering and product lead, partnering directly with the CEO on strategy and roadmap; established engineering cultures from scratch at three startups.",
    },
    {
        title: "AI/LLM in production",
        body: "Built agentic pipelines, MCP tooling, prompt caching, and internal AI developer tools taken from concept to production.",
    },
    {
        title: "Talent development",
        body: "Mentored 15+ engineers across companies and volunteer programs, including guiding several through career transitions into software engineering.",
    },
];

export interface Experience {
    id: string;
    company: string;
    role: string;
    period: string;
    /** Short line used in condensed views. */
    summary: string;
    bullets: {label: string; body: string}[];
}

/** Reverse-chronological, matching the PDF exactly. */
export const EXPERIENCE: Experience[] = [
    {
        id: "pinnacle",
        company: "Pinnacle",
        role: "Founding Senior Full-Stack Engineer",
        period: "Dec 2023 – Present",
        summary: "Second employee. Scaled from prototype to a funded, enterprise-ready product.",
        bullets: [
            {
                label: "Scope and organizational growth",
                body: "Joined as the second person at the company (hired as the design/frontend engineer); expanded ownership to most of the backend, prototyping, and product as the company scaled to 8-10+ people with engineering growing to 3 engineers.",
            },
            {
                label: "Executive collaboration and fundraising",
                body: "Worked directly with the CEO on product strategy, prototyping, and roadmap; delivered the technical milestones foundational to securing $2.5M in seed funding.",
            },
            {
                label: "AI cost leadership",
                body: "Designed and shipped production LLM systems, including agentic pipelines and MCP tooling; implemented a caching strategy that cut overall AI costs by ~60%.",
            },
            {
                label: "Performance turnaround",
                body: "Reduced load times on key cross-team pages from 16s to under 3s through database indexing and targeted optimization strategies.",
            },
            {
                label: "Internal AI tooling",
                body: "Built EAR, an internal engineering-intelligence tool that ingests pull requests and exposes expertise discovery to the organization over MCP.",
            },
            {
                label: "Zero-downtime auth migration",
                body: "Architected and executed a hybrid JWT/WorkOS SSO authentication system with automated token rotation, migrated with zero downtime, unlocking enterprise SSO requirements.",
            },
            {
                label: "New market channel",
                body: "Led the end-to-end build and launch of a native Microsoft Teams application, opening a new enterprise user-acquisition channel.",
            },
            {
                label: "Architecture strategy",
                body: "Proposed a Centralized Data Contracts architecture (Protocol Buffers) standardizing schemas across the stack, reducing integration errors and improving type safety.",
            },
            {
                label: "Engineering operations",
                body: "Established sprint rituals, ticket-sizing methodology (Linear), and CI/CD workflows (GitHub Actions) that became the team's standard operating model.",
            },
        ],
    },
    {
        id: "kepler-savings",
        company: "Kepler Savings",
        role: "Senior Founding Engineer",
        period: "Jun 2023 – Oct 2023",
        summary: "Set the foundational frontend architecture and built interactive financial data products.",
        bullets: [
            {
                label: "Technical strategy",
                body: "Led stack evaluations and engineering best-practice decisions with founders and design/backend leadership; set the foundational frontend architecture (React, TypeScript).",
            },
            {
                label: "Data products",
                body: "Built interactive financial data visualization with D3.js and drove pre-launch performance optimization that substantially improved responsiveness.",
            },
        ],
    },
    {
        id: "market-caviar",
        company: "Market Caviar",
        role: "Founder & Principal Engineer",
        period: "Apr 2022 – Apr 2023",
        summary: "Founded a fintech startup; led a team of 3 and operated the full production stack.",
        bullets: [
            {
                label: "Company leadership",
                body: "Founded a fintech startup; hired and led a team of 3 developers, set product and technical strategy, and negotiated data partnerships.",
            },
            {
                label: "Product and platform",
                body: "Shipped ML-driven market analysis and live graphing for retail trading decisions on a stack spanning Next.js, AWS, Stripe, Redis, and a Linux/NGINX production environment I deployed and operated.",
            },
        ],
    },
    {
        id: "chingu",
        company: "Chingu Voyages",
        role: "Frontend Engineer & Team Lead (Volunteer)",
        period: "Apr 2023 – Jun 2023",
        summary: "Led a 5-developer agile team and mentored junior engineers.",
        bullets: [
            {
                label: "Team leadership and mentorship",
                body: "Led a 5-developer agile team to deliver a full-stack web application; mentored junior developers in React, testing, and user-centered design.",
            },
        ],
    },
    {
        id: "fast",
        company: "Fast",
        role: "Software Engineer II",
        period: "Feb 2020 – Apr 2022",
        summary: "Built one-click checkout used by numerous online retailers.",
        bullets: [
            {
                label: "Core product",
                body: "Contributed to Fast's one-click checkout used by numerous online retailers; partnered with design on user-centered payment experiences.",
            },
            {
                label: "Performance and analytics",
                body: "Drove performance work via profiling and caching strategies, and used platform analytics to prioritize high-impact improvements.",
            },
        ],
    },
    {
        id: "freelance",
        company: "Self-Employed",
        role: "Freelance Software Engineer",
        period: "Nov 2017 – Feb 2020",
        summary: "Delivered web and mobile applications, including applied machine learning, for a range of clients.",
        bullets: [
            {
                label: "Client delivery",
                body: "Built websites and applications for diverse clients (React, React Native, Next.js, Node, Python), including applied machine-learning solutions (scikit-learn, TensorFlow).",
            },
        ],
    },
];

export interface SkillCategory {
    id: string;
    name: string;
    skills: string[];
}

/** Mirrors the résumé's Core Competencies block. */
export const SKILL_CATEGORIES: SkillCategory[] = [
    {
        id: "product-frontend",
        name: "Product & Frontend",
        skills: ["Product discovery", "Prototyping", "React", "Next.js", "TypeScript", "React Native", "D3.js", "Accessibility", "Performance optimization", "Figma"],
    },
    {
        id: "backend-systems",
        name: "Backend & Systems",
        skills: ["Node.js", "Python", "GraphQL", "PostgreSQL", "Redis", "Protocol Buffers", "AWS", "Docker", "Authentication & SSO", "CI/CD"],
    },
    {
        id: "ai-product",
        name: "AI Product Engineering",
        skills: ["Agentic pipelines", "MCP tooling", "Prompt caching", "LLM integration", "Evaluation systems", "Real-time data pipelines", "TensorFlow", "scikit-learn"],
    },
    {
        id: "technical-leadership",
        name: "Technical Leadership",
        skills: ["Architecture & roadmap planning", "Executive collaboration", "Hiring & mentorship", "Engineering operations", "Team process design", "Stakeholder communication", "Testing strategy", "Security practices"],
    },
];

export interface Certification {
    name: string;
    status?: string;
}

export const CERTIFICATIONS: Certification[] = [
    {name: "Professional Product Discovery and Validation (PPDV)"},
    {name: "Meta Front-End Developer Professional Certificate"},
    {name: "Web Security & Penetration Testing"},
    {name: "Google Cybersecurity Professional", status: "In progress"},
];

export const LANGUAGES = [
    {name: "English", level: "Native"},
    {name: "Spanish", level: "Basic, actively learning"},
];
